'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ArrowLeft, CheckCircle2, XCircle, RotateCcw, Clock, AlertTriangle, ChevronDown, ChevronUp, Search, Star, Building2, Phone, Mail, MapPin, FileText, Shield, ArrowUpRight, Eye, UserCheck, Users, AlertCircle, ChevronRight, Award,  } from 'lucide-react';
import { SUPPLIER_STATUS_STYLES, Supplier, SupplierStatus,  } from '../../components/suppliersData';
import { useRole } from '@/lib/useRole';

// ─── Types ────────────────────────────────────────────────────────────────────

type ApprovalAction = 'approve_active' | 'approve_preferred' | 'reject' | 'escalate' | 'return';

interface ApprovalLevel {
  id: string;
  level: number;
  role: string;
  label: string;
  action: 'approved' | 'rejected' | 'pending' | 'escalated' | 'skipped';
  actor?: string;
  comment?: string;
  date?: string;
}

interface SupplierApprovalRecord {
  supplierId: string;
  supplier: Supplier;
  submittedBy: string;
  submittedDate: string;
  requestedStatus: 'active' | 'preferred';
  justification: string;
  levels: ApprovalLevel[];
  currentLevel: number;
  priority: 'normal' | 'high' | 'urgent';
  daysInQueue: number;
  complianceChecked: boolean;
  documentsVerified: boolean;
  evaluationCompleted: boolean;
}

// ─── Mock Approval Records ────────────────────────────────────────────────────

const NILE_AGRO_SUPPLIER: Supplier = {
  id: 'sup-005',
  supplierCode: 'SUP-00005',
  name: 'Nile Agro Chemicals Ltd',
  tradingName: 'Nile Agro',
  supplierType: 'Distributor',
  categories: ['Disinfectants', 'Chemicals', 'Cleaning Materials', 'PPE'],
  registrationNumber: 'UG-REG-2020-0312',
  taxNumber: 'TIN-1006789012',
  country: 'Uganda',
  district: 'Kampala',
  city: 'Kampala',
  physicalAddress: 'Plot 23, Nakawa Industrial Area, Kampala',
  email: 'orders@nileagro.co.ug',
  phone: '+256 705 678 901',
  paymentTerms: 'Cash',
  currency: 'UGX',
  overallScore: 75,
  rating: 3.9,
  preferred: false,
  complianceStatus: 'pending',
  status: 'pending_approval',
  totalSpend: 8200000,
  outstandingBalance: 0,
  openPOs: 0,
  contacts: [{ id: 'c-006', supplierId: 'sup-005', fullName: 'Nakato Prossy', jobTitle: 'Sales Representative', phone: '+256 705 678 901', email: 'prossy@nileagro.co.ug', isPrimary: true }],
  documents: [{ id: 'd-010', supplierId: 'sup-005', documentType: 'Trading License', status: 'pending_verification' }],
  createdAt: '2026-06-01T08:00:00Z',
  updatedAt: '2026-08-01T10:00:00Z',
};

const ABC_FEEDS_SUPPLIER: Supplier = {
  id: 'sup-001',
  supplierCode: 'SUP-00001',
  name: 'ABC Feeds Uganda Ltd',
  tradingName: 'ABC Feeds',
  supplierType: 'Manufacturer',
  categories: ['Feed', 'Feed Ingredients', 'Feed Supplements'],
  registrationNumber: 'UG-REG-2018-0045',
  taxNumber: 'TIN-1002345678',
  country: 'Uganda',
  district: 'Kampala',
  city: 'Kampala',
  physicalAddress: 'Plot 45, Industrial Area, Kampala',
  email: 'procurement@abcfeeds.co.ug',
  phone: '+256 700 123 456',
  paymentTerms: '30 Days',
  creditDays: 30,
  currency: 'UGX',
  overallScore: 94,
  rating: 4.7,
  preferred: true,
  complianceStatus: 'compliant',
  status: 'preferred',
  totalSpend: 187500000,
  outstandingBalance: 12500000,
  openPOs: 2,
  onTimeDeliveryPct: 94,
  qualityScore: 96,
  lastPurchaseDate: '2026-08-28',
  contacts: [{ id: 'c-001', supplierId: 'sup-001', fullName: 'Mukasa David', jobTitle: 'Sales Manager', phone: '+256 700 123 456', email: 'david@abcfeeds.co.ug', isPrimary: true }],
  documents: [
    { id: 'd-001', supplierId: 'sup-001', documentType: 'Certificate of Incorporation', documentNumber: 'CI-2018-0045', issueDate: '2018-03-15', expiryDate: '2028-03-15', status: 'valid' },
    { id: 'd-002', supplierId: 'sup-001', documentType: 'Trading License', documentNumber: 'TL-2026-0123', issueDate: '2026-01-01', expiryDate: '2026-12-31', status: 'valid' },
    { id: 'd-003', supplierId: 'sup-001', documentType: 'Tax Clearance', documentNumber: 'TC-2026-0456', issueDate: '2026-01-15', expiryDate: '2026-12-31', status: 'valid' },
  ],
  createdAt: '2022-01-15T08:00:00Z',
  updatedAt: '2026-08-28T14:00:00Z',
};

const MOCK_APPROVAL_RECORDS: SupplierApprovalRecord[] = [
  {
    supplierId: 'sup-005',
    supplier: NILE_AGRO_SUPPLIER,
    submittedBy: 'Nakato Sarah (Procurement Officer)',
    submittedDate: '2026-08-25',
    requestedStatus: 'active',
    justification: 'Nile Agro Chemicals has completed onboarding, submitted all required documents, and passed initial compliance review. They supply critical disinfectants and PPE needed for biosecurity operations.',
    priority: 'high',
    daysInQueue: 11,
    complianceChecked: true,
    documentsVerified: false,
    evaluationCompleted: false,
    currentLevel: 2,
    levels: [
      { id: 'al-001', level: 1, role: 'Procurement Officer', label: 'Procurement Officer', action: 'approved', actor: 'Nakato Sarah', comment: 'Supplier has completed registration and submitted required documents. Recommending for approval.', date: '2026-08-25' },
      { id: 'al-002', level: 2, role: 'Compliance Review', label: 'Compliance Review', action: 'pending', actor: undefined, comment: undefined, date: undefined },
      { id: 'al-003', level: 3, role: 'Finance Verification', label: 'Finance Verification', action: 'pending', actor: undefined, comment: undefined, date: undefined },
      { id: 'al-004', level: 4, role: 'Procurement Manager', label: 'Procurement Manager', action: 'pending', actor: undefined, comment: undefined, date: undefined },
    ],
  },
  {
    supplierId: 'sup-new-001',
    supplier: {
      id: 'sup-new-001',
      supplierCode: 'SUP-00006',
      name: 'Kampala Packaging Ltd',
      tradingName: 'KPL',
      supplierType: 'Manufacturer',
      categories: ['Egg Trays', 'Packaging'],
      registrationNumber: 'UG-REG-2024-0567',
      taxNumber: 'TIN-1007890123',
      country: 'Uganda',
      district: 'Kampala',
      city: 'Kampala',
      physicalAddress: 'Plot 34, Bugolobi Industrial Park, Kampala',
      email: 'sales@kpl.co.ug',
      phone: '+256 706 789 012',
      paymentTerms: '30 Days',
      creditDays: 30,
      creditLimit: 10000000,
      currency: 'UGX',
      overallScore: 0,
      rating: 0,
      preferred: false,
      complianceStatus: 'pending',
      status: 'pending_approval',
      totalSpend: 0,
      outstandingBalance: 0,
      openPOs: 0,
      contacts: [
        { id: 'c-new-001', supplierId: 'sup-new-001', fullName: 'Kiggundu Moses', jobTitle: 'Sales Manager', phone: '+256 706 789 012', email: 'moses@kpl.co.ug', isPrimary: true },
      ],
      documents: [
        { id: 'd-new-001', supplierId: 'sup-new-001', documentType: 'Certificate of Incorporation', documentNumber: 'CI-2024-0567', issueDate: '2024-03-10', expiryDate: '2034-03-10', status: 'valid' },
        { id: 'd-new-002', supplierId: 'sup-new-001', documentType: 'Trading License', documentNumber: 'TL-2026-0456', issueDate: '2026-01-01', expiryDate: '2026-12-31', status: 'valid' },
        { id: 'd-new-003', supplierId: 'sup-new-001', documentType: 'Tax Clearance', status: 'pending_verification' },
      ],
      createdAt: '2026-08-20T08:00:00Z',
      updatedAt: '2026-09-01T10:00:00Z',
    },
    submittedBy: 'Mugisha Robert (Procurement Manager)',
    submittedDate: '2026-09-01',
    requestedStatus: 'active',
    justification: 'Kampala Packaging Ltd is a new local manufacturer of egg trays and packaging materials. They offer competitive pricing and can supply on short notice. Onboarding completed successfully.',
    priority: 'normal',
    daysInQueue: 4,
    complianceChecked: true,
    documentsVerified: true,
    evaluationCompleted: false,
    currentLevel: 3,
    levels: [
      { id: 'al-005', level: 1, role: 'Procurement Officer', label: 'Procurement Officer', action: 'approved', actor: 'Waiswa Peter', comment: 'Documents verified. Supplier meets basic requirements.', date: '2026-09-01' },
      { id: 'al-006', level: 2, role: 'Compliance Review', label: 'Compliance Review', action: 'approved', actor: 'Nakato Sarah', comment: 'Compliance check passed. Tax clearance pending but not blocking.', date: '2026-09-02' },
      { id: 'al-007', level: 3, role: 'Finance Verification', label: 'Finance Verification', action: 'pending', actor: undefined, comment: undefined, date: undefined },
      { id: 'al-008', level: 4, role: 'Procurement Manager', label: 'Procurement Manager', action: 'pending', actor: undefined, comment: undefined, date: undefined },
    ],
  },
  {
    supplierId: 'sup-new-002',
    supplier: {
      id: 'sup-new-002',
      supplierCode: 'SUP-00007',
      name: 'Agri-Tech Solutions Uganda',
      tradingName: 'Agri-Tech',
      supplierType: 'Equipment Supplier',
      categories: ['Poultry Equipment', 'Incubators', 'Brooders'],
      registrationNumber: 'UG-REG-2023-0789',
      taxNumber: 'TIN-1008901234',
      country: 'Uganda',
      district: 'Wakiso',
      city: 'Entebbe',
      physicalAddress: 'Plot 56, Entebbe Road, Wakiso',
      email: 'info@agritechug.co.ug',
      phone: '+256 707 890 123',
      paymentTerms: '14 Days',
      creditDays: 14,
      currency: 'UGX',
      overallScore: 0,
      rating: 0,
      preferred: false,
      complianceStatus: 'pending',
      status: 'pending_approval',
      totalSpend: 0,
      outstandingBalance: 0,
      openPOs: 0,
      contacts: [
        { id: 'c-new-002', supplierId: 'sup-new-002', fullName: 'Ssali Emmanuel', jobTitle: 'Director', phone: '+256 707 890 123', email: 'emmanuel@agritechug.co.ug', isPrimary: true },
      ],
      documents: [
        { id: 'd-new-004', supplierId: 'sup-new-002', documentType: 'Certificate of Incorporation', documentNumber: 'CI-2023-0789', issueDate: '2023-07-15', expiryDate: '2033-07-15', status: 'valid' },
        { id: 'd-new-005', supplierId: 'sup-new-002', documentType: 'Trading License', documentNumber: 'TL-2026-0567', issueDate: '2026-01-01', expiryDate: '2026-12-31', status: 'valid' },
      ],
      createdAt: '2026-09-03T08:00:00Z',
      updatedAt: '2026-09-03T08:00:00Z',
    },
    submittedBy: 'Kato Finance (Finance Officer)',
    submittedDate: '2026-09-03',
    requestedStatus: 'active',
    justification: 'Agri-Tech Solutions is a specialized poultry equipment supplier with competitive pricing on incubators and brooders. Needed for upcoming farm expansion project.',
    priority: 'urgent',
    daysInQueue: 2,
    complianceChecked: false,
    documentsVerified: true,
    evaluationCompleted: false,
    currentLevel: 1,
    levels: [
      { id: 'al-009', level: 1, role: 'Procurement Officer', label: 'Procurement Officer', action: 'pending', actor: undefined, comment: undefined, date: undefined },
      { id: 'al-010', level: 2, role: 'Compliance Review', label: 'Compliance Review', action: 'pending', actor: undefined, comment: undefined, date: undefined },
      { id: 'al-011', level: 3, role: 'Finance Verification', label: 'Finance Verification', action: 'pending', actor: undefined, comment: undefined, date: undefined },
      { id: 'al-012', level: 4, role: 'Procurement Manager', label: 'Procurement Manager', action: 'pending', actor: undefined, comment: undefined, date: undefined },
    ],
  },
];

const RECENTLY_PROCESSED: SupplierApprovalRecord[] = [
  {
    supplierId: 'sup-001',
    supplier: ABC_FEEDS_SUPPLIER,
    submittedBy: 'Mugisha Robert (Procurement Manager)',
    submittedDate: '2026-07-10',
    requestedStatus: 'preferred',
    justification: 'ABC Feeds has consistently scored above 90% for 3 consecutive quarters. Recommending upgrade to Preferred status.',
    priority: 'normal',
    daysInQueue: 0,
    complianceChecked: true,
    documentsVerified: true,
    evaluationCompleted: true,
    currentLevel: 4,
    levels: [
      { id: 'al-013', level: 1, role: 'Procurement Officer', label: 'Procurement Officer', action: 'approved', actor: 'Nakato Sarah', comment: 'Fully supports preferred status upgrade.', date: '2026-07-10' },
      { id: 'al-014', level: 2, role: 'Compliance Review', label: 'Compliance Review', action: 'approved', actor: 'Waiswa Peter', comment: 'All documents valid and compliant.', date: '2026-07-11' },
      { id: 'al-015', level: 3, role: 'Finance Verification', label: 'Finance Verification', action: 'approved', actor: 'Kato Finance', comment: 'No outstanding issues. Payment history excellent.', date: '2026-07-12' },
      { id: 'al-016', level: 4, role: 'Procurement Manager', label: 'Procurement Manager', action: 'approved', actor: 'Okello James', comment: 'Approved for Preferred status. Framework agreement to follow.', date: '2026-07-14' },
    ],
  },
];

// ─── Priority Styles ──────────────────────────────────────────────────────────

const PRIORITY_STYLES = {
  normal: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Normal' },
  high: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'High' },
  urgent: { bg: 'bg-red-50', text: 'text-red-700', label: 'Urgent' },
};

// ─── Approval Level Step ──────────────────────────────────────────────────────

function ApprovalLevelStep({ level, isLast }: { level: ApprovalLevel; isLast: boolean }) {
  const isApproved = level.action === 'approved';
  const isRejected = level.action === 'rejected';
  const isPending = level.action === 'pending';
  const isEscalated = level.action === 'escalated';

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
          isApproved ? 'bg-green-500 border-green-500' : isRejected ?'bg-red-500 border-red-500': isEscalated ?'bg-purple-500 border-purple-500': isPending ?'bg-white border-amber-400' : 'bg-white border-gray-200'
        }`}>
          {isApproved ? <CheckCircle2 size={14} className="text-white" /> :
           isRejected ? <XCircle size={14} className="text-white" /> :
           isEscalated ? <ArrowUpRight size={14} className="text-white" /> :
           isPending ? <Clock size={14} className="text-amber-500" /> :
           <div className="w-2 h-2 rounded-full bg-gray-300" />}
        </div>
        {!isLast && <div className={`w-0.5 flex-1 mt-1 ${isApproved ? 'bg-green-300' : 'bg-gray-200'}`} style={{ minHeight: '24px' }} />}
      </div>
      <div className="pb-4 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-bold ${isApproved ? 'text-green-700' : isRejected ? 'text-red-700' : isPending ? 'text-amber-700' : 'text-gray-500'}`}>
            Level {level.level}: {level.label}
          </span>
          {level.actor && <span className="text-xs text-gray-500">— {level.actor}</span>}
          {level.date && <span className="text-xs text-gray-400">{level.date}</span>}
        </div>
        {level.comment && (
          <div className="mt-1 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
            "{level.comment}"
          </div>
        )}
        {isPending && (
          <div className="mt-1 text-xs text-amber-600 font-medium">Awaiting action</div>
        )}
      </div>
    </div>
  );
}

// ─── Approval Card ────────────────────────────────────────────────────────────

function ApprovalCard({
  record,
  onAction,
}: {
  record: SupplierApprovalRecord;
  onAction: (supplierId: string, action: ApprovalAction, comment: string, targetStatus?: 'active' | 'preferred') => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [action, setAction] = useState<ApprovalAction | null>(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [targetStatus, setTargetStatus] = useState<'active' | 'preferred'>(record.requestedStatus);

  const { supplier, levels, currentLevel, priority, daysInQueue } = record;
  const st = SUPPLIER_STATUS_STYLES[supplier.status];
  const pri = PRIORITY_STYLES[priority];
  const pendingLevel = levels.find(l => l.action === 'pending');
  const completedLevels = levels.filter(l => l.action === 'approved').length;
  const progressPct = Math.round((completedLevels / levels.length) * 100);

  function handleSubmit() {
    const requiresComment = action === 'reject' || action === 'return' || action === 'escalate';
    if (requiresComment && !comment.trim()) {
      setCommentError('A comment is required for this action.');
      return;
    }
    setCommentError('');
    onAction(record.supplierId, action!, comment, targetStatus);
    setAction(null);
    setComment('');
  }

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${
      priority === 'urgent' ? 'border-red-200 shadow-sm' :
      priority === 'high' ? 'border-amber-200 shadow-sm' : 'border-gray-100'
    }`}>
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
          priority === 'urgent' ? 'bg-red-100 text-red-700' :
          priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
        }`}>
          {supplier.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">{supplier.name}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pri.bg} ${pri.text}`}>{pri.label}</span>
            {daysInQueue > 7 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 flex items-center gap-1">
                <AlertCircle size={10} /> {daysInQueue}d in queue
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {supplier.supplierCode} · {supplier.categories.slice(0, 2).join(', ')} · Submitted by {record.submittedBy}
          </div>
          <div className="text-xs text-gray-600 mt-1 line-clamp-2">{record.justification}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <div className="text-xs text-gray-500">
            Requesting: <span className={`font-bold ${record.requestedStatus === 'preferred' ? 'text-purple-700' : 'text-green-700'}`}>
              {record.requestedStatus === 'preferred' ? '⭐ Preferred' : '✓ Active'}
            </span>
          </div>
          <div className="text-xs text-gray-400">{record.submittedDate}</div>
          <button onClick={() => setExpanded(!expanded)} className="p-1 rounded hover:bg-gray-100 transition-colors mt-1">
            {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-4 pb-3">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
            <div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="text-xs text-gray-500 flex-shrink-0">{completedLevels}/{levels.length} levels</span>
        </div>
        {/* Level Steps (compact) */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {levels.map((lv, idx) => (
            <React.Fragment key={lv.id}>
              {idx > 0 && <ChevronRight size={10} className="text-gray-300 flex-shrink-0" />}
              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg flex-shrink-0 text-xs font-semibold ${
                lv.action === 'approved' ? 'bg-green-50 text-green-700' :
                lv.action === 'rejected' ? 'bg-red-50 text-red-700' :
                lv.action === 'escalated' ? 'bg-purple-50 text-purple-700' :
                lv.action === 'pending'&& lv.level === currentLevel ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-50 text-gray-400'
              }`}>
                {lv.action === 'approved' ? <CheckCircle2 size={10} /> :
                 lv.action === 'rejected' ? <XCircle size={10} /> :
                 lv.action === 'pending' && lv.level === currentLevel ? <Clock size={10} /> :
                 <div className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />}
                <span>{lv.label.split(' ')[0]}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/30">
          {/* Supplier Info */}
          <div className="px-4 py-3 grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Supplier Details</div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Building2 size={12} className="text-gray-400" />
                  <span>{supplier.supplierType}</span>
                </div>
                {supplier.phone && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Phone size={12} className="text-gray-400" />
                    <span>{supplier.phone}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Mail size={12} className="text-gray-400" />
                    <span className="truncate">{supplier.email}</span>
                  </div>
                )}
                {supplier.physicalAddress && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <MapPin size={12} className="text-gray-400" />
                    <span className="truncate">{supplier.city}, {supplier.country}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 border border-gray-100">
              <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Checklist</div>
              <div className="space-y-1.5">
                {[
                  { label: 'Compliance Checked', done: record.complianceChecked },
                  { label: 'Documents Verified', done: record.documentsVerified },
                  { label: 'Evaluation Completed', done: record.evaluationCompleted },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2 text-xs">
                    {item.done
                      ? <CheckCircle2 size={12} className="text-green-500" />
                      : <div className="w-3 h-3 rounded-full border-2 border-gray-300" />}
                    <span className={item.done ? 'text-gray-700' : 'text-gray-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Documents */}
          {supplier.documents && supplier.documents.length > 0 && (
            <div className="px-4 pb-3">
              <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Documents</div>
              <div className="flex flex-wrap gap-2">
                {supplier.documents.map(doc => {
                  const docStyles = {
                    valid: 'bg-green-50 text-green-700 border-green-200',
                    expiring_soon: 'bg-amber-50 text-amber-700 border-amber-200',
                    expired: 'bg-red-50 text-red-700 border-red-200',
                    pending_verification: 'bg-blue-50 text-blue-700 border-blue-200',
                    rejected: 'bg-red-50 text-red-600 border-red-200',
                  };
                  return (
                    <div key={doc.id} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium ${docStyles[doc.status]}`}>
                      <FileText size={10} />
                      <span>{doc.documentType}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full Approval Timeline */}
          <div className="px-4 pb-3">
            <div className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">Approval Timeline</div>
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              {levels.map((lv, idx) => (
                <ApprovalLevelStep key={lv.id} level={lv} isLast={idx === levels.length - 1} />
              ))}
            </div>
          </div>

          {/* Action Panel */}
          {pendingLevel && (
            <div className="px-4 pb-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-xs font-bold text-gray-700 mb-3">
                  Action Required — Level {pendingLevel.level}: {pendingLevel.label}
                </div>

                {/* Target Status Selector (only for final approval) */}
                {pendingLevel.level === levels.length && (
                  <div className="mb-3">
                    <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Approve as:</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTargetStatus('active')}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                          targetStatus === 'active' ? 'bg-green-50 border-green-400 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <UserCheck size={12} />
                        Active Supplier
                      </button>
                      <button
                        onClick={() => setTargetStatus('preferred')}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                          targetStatus === 'preferred' ? 'bg-purple-50 border-purple-400 text-purple-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                        }`}
                      >
                        <Star size={12} />
                        Preferred Supplier
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { key: 'approve_active' as ApprovalAction, label: pendingLevel.level === levels.length ? `Approve as ${targetStatus === 'preferred' ? 'Preferred' : 'Active'}` : 'Approve & Forward', icon: CheckCircle2, style: 'bg-green-600 hover:bg-green-700 text-white' },
                    { key: 'return' as ApprovalAction, label: 'Return for Revision', icon: RotateCcw, style: 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200' },
                    { key: 'escalate' as ApprovalAction, label: 'Escalate', icon: ArrowUpRight, style: 'bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200' },
                    { key: 'reject' as ApprovalAction, label: 'Reject', icon: XCircle, style: 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200' },
                  ].map(btn => {
                    const BtnIcon = btn.icon;
                    return (
                      <button
                        key={btn.key}
                        onClick={() => setAction(action === btn.key ? null : btn.key)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${btn.style} ${action === btn.key ? 'ring-2 ring-offset-1 ring-current' : ''}`}
                      >
                        <BtnIcon size={12} />
                        {btn.label}
                      </button>
                    );
                  })}
                </div>

                {/* Comment Box */}
                {action && (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-600 block">
                      Comment {(action === 'reject' || action === 'return' || action === 'escalate') ? <span className="text-red-500">*</span> : '(optional)'}
                    </label>
                    <textarea
                      value={comment}
                      onChange={e => { setComment(e.target.value); setCommentError(''); }}
                      rows={3}
                      placeholder={
                        action === 'reject' ? 'Provide reason for rejection...' :
                        action === 'return' ? 'Specify what needs to be revised...' :
                        action === 'escalate'? 'Explain why this is being escalated...' : 'Optional comment...'
                      }
                      className="w-full text-xs border border-gray-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {commentError && <p className="text-xs text-red-600">{commentError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-xl hover:bg-gray-800 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => { setAction(null); setComment(''); setCommentError(''); }}
                        className="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SupplierApprovalsContent() {
    const { can: hasPermission } = useRole();
  const canApprove = hasPermission('suppliers.approve');

  const [records, setRecords] = useState<SupplierApprovalRecord[]>(MOCK_APPROVAL_RECORDS);
  const [recentlyProcessed, setRecentlyProcessed] = useState<SupplierApprovalRecord[]>(RECENTLY_PROCESSED);
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState<'all' | 'normal' | 'high' | 'urgent'>('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState<'pending' | 'processed'>('pending');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  function showToast(message: string, type: 'success' | 'error' = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  function handleAction(supplierId: string, action: ApprovalAction, comment: string, targetStatus?: 'active' | 'preferred') {
    setRecords(prev => {
      const updated = prev.map(r => {
        if (r.supplierId !== supplierId) return r;
        const pendingIdx = r.levels.findIndex(l => l.action === 'pending');
        if (pendingIdx === -1) return r;

        const newLevels = r.levels.map((lv, idx) => {
          if (idx !== pendingIdx) return lv;
          return {
            ...lv,
            action: action === 'approve_active' ? 'approved' as const :
                    action === 'approve_preferred' ? 'approved' as const :
                    action === 'reject' ? 'rejected' as const :
                    action === 'escalate' ? 'escalated' as const : 'pending' as const,
            actor: 'Okello James (Current User)',
            comment: comment || undefined,
            date: new Date().toISOString().split('T')[0],
          };
        });

        const isLastLevel = pendingIdx === r.levels.length - 1;
        const isFullyApproved = action === 'approve_active' && isLastLevel;
        const isRejected = action === 'reject';
        const isReturned = action === 'return';

        if (isFullyApproved || isRejected || isReturned) {
          const finalStatus: SupplierStatus = isFullyApproved
            ? (targetStatus === 'preferred' ? 'preferred' : 'active')
            : isRejected ? 'rejected' : 'pending_review';

          const processed: SupplierApprovalRecord = {
            ...r,
            levels: newLevels,
            supplier: { ...r.supplier, status: finalStatus },
          };
          setRecentlyProcessed(rp => [processed, ...rp]);
          return null as unknown as SupplierApprovalRecord;
        }

        return { ...r, levels: newLevels, currentLevel: r.currentLevel + (action === 'approve_active' ? 1 : 0) };
      }).filter(Boolean);

      return updated;
    });

    const actionLabels: Record<ApprovalAction, string> = {
      approve_active: targetStatus === 'preferred' ? 'Supplier approved as Preferred' : 'Supplier approved as Active',
      approve_preferred: 'Supplier approved as Preferred',
      reject: 'Supplier application rejected',
      escalate: 'Approval escalated to next level',
      return: 'Returned for revision',
    };
    showToast(actionLabels[action]);
  }

  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      const matchSearch = !search || r.supplier.name.toLowerCase().includes(search.toLowerCase()) || r.supplier.supplierCode.toLowerCase().includes(search.toLowerCase());
      const matchPriority = filterPriority === 'all' || r.priority === filterPriority;
      const matchCategory = filterCategory === 'all' || r.supplier.categories.includes(filterCategory);
      return matchSearch && matchPriority && matchCategory;
    });
  }, [records, search, filterPriority, filterCategory]);

  const allCategories = Array.from(new Set(records.flatMap(r => r.supplier.categories)));

  const kpis = [
    { label: 'Pending Approvals', value: records.length, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Urgent', value: records.filter(r => r.priority === 'urgent').length, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'High Priority', value: records.filter(r => r.priority === 'high').length, icon: AlertCircle, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Approved This Month', value: recentlyProcessed.filter(r => r.supplier.status === 'active' || r.supplier.status === 'preferred').length, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Preferred Upgrades', value: recentlyProcessed.filter(r => r.supplier.status === 'preferred').length, icon: Award, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-base)' }}>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold flex items-center gap-2 transition-all ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
          {toast.message}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Link href="/suppliers" className="p-2 rounded-xl hover:bg-gray-100 transition-colors mt-0.5">
            <ArrowLeft size={18} className="text-gray-500" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Supplier Approvals</h1>
            <p className="text-sm text-gray-500 mt-0.5">Review and approve pending supplier registrations with multi-level authorization</p>
          </div>
          <Link href="/suppliers/new" className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition-colors">
            <Users size={15} />
            Add Supplier
          </Link>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {kpis.map(kpi => {
            const KpiIcon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${kpi.bg}`}>
                  <KpiIcon size={16} className={kpi.color} />
                </div>
                <div>
                  <div className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</div>
                  <div className="text-xs text-gray-500 leading-tight">{kpi.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {(['pending', 'processed'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'pending' ? `Pending (${records.length})` : `Processed (${recentlyProcessed.length})`}
            </button>
          ))}
        </div>

        {activeTab === 'pending' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-48">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
              <select
                value={filterPriority}
                onChange={e => setFilterPriority(e.target.value as typeof filterPriority)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
              </select>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="all">All Categories</option>
                {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Permission Warning */}
            {!canApprove && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                <Shield size={16} className="text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">You have view-only access. Approval actions require the <strong>suppliers.approve</strong> permission.</p>
              </div>
            )}

            {/* Cards */}
            {filteredRecords.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <CheckCircle2 size={40} className="text-green-400 mx-auto mb-3" />
                <div className="text-base font-semibold text-gray-700">No pending approvals</div>
                <div className="text-sm text-gray-400 mt-1">All supplier applications have been processed.</div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRecords.map(record => (
                  <ApprovalCard
                    key={record.supplierId}
                    record={record}
                    onAction={canApprove ? handleAction : () => showToast('You do not have permission to approve suppliers.', 'error')}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'processed' && (
          <div className="space-y-4">
            {recentlyProcessed.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <FileText size={40} className="text-gray-300 mx-auto mb-3" />
                <div className="text-base font-semibold text-gray-700">No processed approvals yet</div>
              </div>
            ) : (
              recentlyProcessed.map(record => {
                const st = SUPPLIER_STATUS_STYLES[record.supplier.status];
                const completedLevels = record.levels.filter(l => l.action === 'approved').length;
                return (
                  <div key={record.supplierId} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                        record.supplier.status === 'active' || record.supplier.status === 'preferred' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {record.supplier.status === 'active' || record.supplier.status === 'preferred'
                          ? <CheckCircle2 size={18} />
                          : <XCircle size={18} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-gray-900">{record.supplier.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{record.supplier.supplierCode} · {record.supplier.categories.slice(0, 2).join(', ')}</div>
                        <div className="flex items-center gap-3 mt-2">
                          {record.levels.map((lv, idx) => (
                            <React.Fragment key={lv.id}>
                              {idx > 0 && <ChevronRight size={10} className="text-gray-300" />}
                              <div className={`flex items-center gap-1 text-xs font-semibold ${
                                lv.action === 'approved' ? 'text-green-700' :
                                lv.action === 'rejected' ? 'text-red-700' : 'text-gray-400'
                              }`}>
                                {lv.action === 'approved' ? <CheckCircle2 size={10} /> :
                                 lv.action === 'rejected' ? <XCircle size={10} /> :
                                 <div className="w-2 h-2 rounded-full bg-gray-300" />}
                                {lv.label.split(' ')[0]}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="text-xs text-gray-400">{record.submittedDate}</div>
                        <Link href={`/suppliers/${record.supplierId}`} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                          <Eye size={11} /> View
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Workflow Guide */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={15} className="text-blue-600" />
            <span className="text-sm font-bold text-gray-800">Approval Workflow</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { level: 1, role: 'Procurement Officer', desc: 'Initial review & document check' },
              { level: 2, role: 'Compliance Review', desc: 'Regulatory & compliance verification' },
              { level: 3, role: 'Finance Verification', desc: 'Credit terms & financial check' },
              { level: 4, role: 'Procurement Manager', desc: 'Final approval → Active or Preferred' },
            ].map((step, idx) => (
              <div key={step.level} className="relative">
                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <div className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mb-2">{step.level}</div>
                  <div className="text-xs font-semibold text-gray-800">{step.role}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{step.desc}</div>
                </div>
                {idx < 3 && <ChevronRight size={14} className="absolute -right-2 top-1/2 -translate-y-1/2 text-gray-300 hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
